import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Home } from "lucide-react";

const COLORS = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1"];

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    family_name: "",
    admin_name: "",
    email: "",
    password: "",
    avatar_color: "#E8B96B",
  });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Family created! Welcome to FamilyOS.");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--hh-cream)" }}>
      <div className="w-full max-w-md nb-card p-9 hh-fade-up">
        <div className="flex items-center gap-3 mb-7">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: "var(--hh-sage)", color: "var(--hh-card)" }}
          >
            <Home strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>familytable</h1>
            <p className="text-xs uppercase tracking-wider" style={{ color: "var(--hh-ink-soft)" }}>Get started</p>
          </div>
        </div>

        <h2 className="text-4xl font-extrabold mb-1 leading-tight" style={{ fontFamily: "var(--hh-display)" }}>Create your family</h2>
        <p className="text-sm mb-6" style={{ color: "var(--hh-ink-soft)" }}>One account. Everyone at the table.</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Family name</label>
            <input value={form.family_name} onChange={set("family_name")} required className="nb-input w-full" data-testid="register-family-name" placeholder="The Smiths" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Your name</label>
            <input value={form.admin_name} onChange={set("admin_name")} required className="nb-input w-full" data-testid="register-admin-name" placeholder="Jane Smith" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Email</label>
            <input type="email" value={form.email} onChange={set("email")} required className="nb-input w-full" data-testid="register-email" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Password</label>
            <input type="password" value={form.password} onChange={set("password")} required minLength={6} className="nb-input w-full" data-testid="register-password" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Avatar color</label>
            <div className="flex gap-2.5">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm({ ...form, avatar_color: c })}
                  className="w-10 h-10 rounded-full transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    boxShadow: form.avatar_color === c
                      ? "inset 0 0 0 2px var(--hh-ink), 0 0 0 3px rgba(42,31,23,0.10)"
                      : "inset 0 0 0 1px rgba(42,31,23,0.10)",
                  }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} className="nb-btn nb-btn-ink w-full py-3 text-base mt-2" data-testid="register-submit">
            {loading ? "Creating…" : "Create family"}
          </button>
        </form>

        <p className="text-sm text-center mt-7" style={{ color: "var(--hh-ink-soft)" }}>
          Already have an account?{" "}
          <Link to="/login" className="font-semibold" style={{ color: "var(--hh-terracotta)", borderBottom: "1px solid var(--hh-terracotta)" }} data-testid="link-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
